import React, { useContext, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  CAlert,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CCollapse,
  CDataTable,
  CDropdown,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CRow,
  CSwitch,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import * as pluralize from 'pluralize';
import { sortBy } from 'lodash';
import { connect, DispatchProp } from 'react-redux';
import usePortal from 'react-useportal';
import {
  BulkUploadTaskContainer,
  BulkUploadTaskUpdate,
  JobsUploadStatus,
  TransformedBulkTask, UploadStatus,
} from '../../shared/models/jobs';
import { ConfigContext } from '../../shared/contexts/journeyConfig.context';
import { ClearRows, LoadRows, LoadUploads, ProcessUpload, UpdateRow } from '../actions';
import { RootState } from '../../store/state';
import ButtonWithConfirmation from '../../shared/components/ButtonWithConfirmation';
import useInterval from '../../shared/hooks/useInterval.hook';

const REFRESH_UPLOAD_INTERVAL = 2000; // Two seconds

const FIELDS = (labels: { agent: string, task: string }) => ([
  {
    key: 'line',
    label: 'Row #',
    filter: true,
  },
  {
    key: 'task_id',
    label: `${labels.task} ID`,
  },
  {
    key: 'agent_id',
    label: `${labels.agent} ID`,
  },
  {
    key: 'customerName',
    label: 'Customer Name',
  },
  {
    key: 'destination',
    label: 'Destination',
  },
  {
    key: 'skip',
    label: 'Skipped?',
    _classes: 'text-center',
  },
  {
    key: 'processed',
    label: 'Processed?',
    _classes: 'text-center',
  },
  {
    key: 'updating',
    label: 'Updating?',
    _classes: 'text-center',
  },
  {
    key: 'hasErrors',
    label: 'Errors?',
    _classes: 'text-center',
  },
  {
    key: 'actions',
    label: '',
    _style: { width: '1%' },
    sorter: false,
    filter: false,
  },
]);

export interface BulkReviewProps extends DispatchProp {
  uploads: JobsUploadStatus[];
  rows: BulkUploadTaskContainer[];
}

const BulkReviewPage = ({ uploads, rows, dispatch }: BulkReviewProps) => {
  const { id: uploadID } = useParams<{id: string}>();
  const labels = useContext(ConfigContext);
  const [showErrors, setShowErrors] = useState<string[]>([]);
  const [errorsOnly, setErrorsOnly] = useState<boolean>(false);
  const [isDone, setIsDone] = useState<boolean>(false);
  const { Portal } = usePortal({
    bindTo: document && document.querySelector('body')
  });

  const history = useHistory();

  useEffect(() => {
    return function cleanup() {
      dispatch(new ClearRows);
    };
  }, [uploadID]);

  useInterval(() => {
    dispatch(new LoadUploads());
    dispatch(new LoadRows(uploadID));
  }, REFRESH_UPLOAD_INTERVAL);

  const handleGoBack = () => {
    history.push('/jobs/upload');
  }

  const handleProcess = async () => {
    await dispatch(new ProcessUpload(uploadID));
  };

  const handleSkip = (id: string, updates: BulkUploadTaskUpdate) => () => {
    dispatch(new UpdateRow(uploadID, id, updates));
  };

  const toggleErrors = (id: string) => () => {
    if (showErrors.indexOf(id) >= 0) {
      setShowErrors(showErrors.filter(taskID => taskID !== id))
    } else {
      setShowErrors([...showErrors, id]);
    }
  };

  const transformedTasks = (rawTasks, onlyErrors) => rawTasks.map((data: BulkUploadTaskContainer) => ({
    id: data.id,
    line: data.line,
    skip: data.skip,
    processed: data.processed,
    errors: data.errors,
    task: data.task,
    isUpdating: data.diff != null,
    hasErrors: data.errors?.length > 0,
    customerName: data.task.CustomerName,
    destination: data.task.DestinationAddress,
  } as TransformedBulkTask)).filter(data => onlyErrors ? data.hasErrors :  true);

  const upload = uploads?.find(item => item.id === uploadID);

  useEffect(() => {
    if (upload?.status === UploadStatus.Done) {
      setIsDone(true);
    }
  }, [upload]);

  return (
    <>
      { isDone && (
        <CRow>
          <CCol>
            <CAlert className='d-block text-center' color='success'>
              Upload has been processed!
              <button type="button" className="btn btn-link btn-go-back" onClick={ () => history.push('/jobs/search') }>
                Go back to { pluralize(labels.task, 2).toLowerCase() }
              </button>
            </CAlert>
          </CCol>
        </CRow>
      ) }
      <CRow>
        <CCol lg="12">
          <CCard>
            <CCardHeader className="border-0">
              <div className='d-flex align-items-center justify-content-between'>
                <div>
                  Review { pluralize(labels.task) }
                </div>
                <div className="card-header-actions d-flex">
                  <div>
                    <button type="button" className="btn btn-link" onClick={ handleGoBack }>
                      Back to bulk uploads
                    </button>
                  </div>

                  <div className="d-flex">
                    <div className="card-header-label">
                      Show errors only?
                    </div>
                      <CSwitch
                        size="lg"
                        color="primary"
                        className="mr-4 ml-2 mt-1"
                        labelOn="on"
                        labelOff="off"
                        checked={ errorsOnly }
                        onChange={ () => setErrorsOnly(!errorsOnly) }
                      />
                  </div>

                  <div>
                    <ButtonWithConfirmation
                      isLoading={ upload?.status === 'processing' }
                      loadingLabel="Processing..."
                      color='primary'
                      onConfirm={ handleProcess }
                      className="bg-color-orange border-0"
                      disabled={ upload === undefined || isDone }
                      dialogTextProps={{
                        title: "Bulk Upload Confirmation",
                        bodyTxt: `Once the upload has completed all ${ pluralize(labels.task).toLowerCase() } will be scheduled and customer reminder notifications will be sent.`,
                      }}
                    >Process</ButtonWithConfirmation>
                  </div>
                </div>
              </div>
            </CCardHeader>

            <div className='task-review-container'>
              <CDataTable
                loading={ upload === undefined }
                items={ rows && sortBy(transformedTasks(rows, errorsOnly), ['line']) }
                fields={ FIELDS(labels) }
                pagination={{
                  addListClass: "ml-2"
                }}
                scopedSlots={{
                  task_id: (item: TransformedBulkTask) => {
                    if (item.task.ID == null) {
                      return (
                        <td className='text-center'>
                          <CIcon name="cil-minus" />
                        </td>
                      );
                    }

                    return (
                      <td className='text-center'>
                        { item.task.ID }
                      </td>
                    );
                  },
                  agent_id: (item: TransformedBulkTask) => {
                    if (item.task.Technician == null) {
                      return (
                        <td className='text-center'>
                          <CIcon name="cil-minus" />
                        </td>
                      );
                    }

                    return (
                      <td>
                        { item.task.Technician }
                      </td>
                    );
                  },
                  skip: (item: TransformedBulkTask) => {
                    return (
                      <td className="text-center">
                        { item.skip ? <CIcon name="cil-check" /> : <CIcon name="cil-minus" /> }
                      </td>
                    );
                  },
                  processed: (item: TransformedBulkTask) => {
                    return (
                      <td className="text-center">
                        { item.processed ? <CIcon name="cil-check" /> : <CIcon name="cil-minus" /> }
                      </td>
                    );
                  },
                  updating: (item: TransformedBulkTask) => {
                    return (
                      <td className="text-center">
                        { item.isUpdating ? <CIcon name="cil-check" /> : <CIcon name="cil-minus" /> }
                      </td>
                    );
                  },
                  hasErrors: (item: TransformedBulkTask) => {
                    return (
                      <td className="text-center">
                        { item.hasErrors ? <CIcon className="text-danger" name="cil-warning" /> : <CIcon name="cil-minus" /> }
                      </td>
                    );
                  },
                  actions: (item: TransformedBulkTask) => {
                    const skip = item.processed && item.hasErrors === false;
                    if (skip) {
                      return (<></>);
                    }

                    return (
                      <td>
                        <CDropdown>
                          <CDropdownToggle
                            caret
                            size="xs"
                            color="primary"
                            className="bg-color-orange border-0"
                          >
                            Actions
                          </CDropdownToggle>
                          <Portal>
                            <CDropdownMenu>
                              { item.hasErrors === false && (
                                <CDropdownItem onClick={ handleSkip(item.id, { skip: !item.skip }) }>
                                  { item.skip ? 'Unskip Row' : 'Skip Row' }
                                </CDropdownItem>
                              ) }
                              { item.hasErrors && (
                                <CDropdownItem onClick={ toggleErrors(item.id) }>
                                  { showErrors.indexOf(item.id) >= 0 ? 'Hide Errors' : 'View Errors' }
                                </CDropdownItem>
                              ) }
                            </CDropdownMenu>
                          </Portal>
                        </CDropdown>
                      </td>
                    );
                  },
                  details: (item: TransformedBulkTask) => {
                    const renderValue = value => {
                      if (!value) {
                        return <small>N/A</small>;
                      }

                      let output = value;
                      if (Array.isArray(value)) {
                        output = value.join(', ');
                      }

                      return <code>{ output }</code>;
                    };

                    return (
                      <CCollapse show={ showErrors.indexOf(item.id) >= 0}>
                        <CCardBody>
                          <div className="row">
                            <div className="col-md-6">
                              <h4>Data</h4>
                              <ul>
                                { Object.keys(item.task).map(key => {
                                  return (
                                    <li key={ key }>
                                      { key } - { renderValue(item.task[key]) }
                                    </li>
                                  );
                                }) }
                              </ul>
                            </div>
                            <div className="col-md-6">
                              <h4>Errors</h4>
                              <ol>
                                { item.errors?.map(error => <li key={ error.type }>{ error.error }</li> ) }
                              </ol>
                            </div>
                          </div>
                        </CCardBody>
                      </CCollapse>
                    );
                  },
                }}
              />
            </div>
          </CCard>
        </CCol>
      </CRow>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  uploads: state?.jobs?.uploads?.data,
  rows: state?.jobs?.rows?.data,
});

export default connect(mapStateToProps)(BulkReviewPage);
